import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { 
  MoreHorizontal, 
  Edit, 
  Trash2, 
  Plus,
  Users
} from 'lucide-react';
import Badge from '../ui/Badge';
import { Card } from '../ui/Card';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuSeparator, 
  DropdownMenuTrigger 
} from '../ui/DropdownMenu';
import { apiService } from '../../services/api';
import { EditRoleModal } from './EditRoleModal';
import { AddRoleModal } from './AddRoleModal';
import { TeamRolesGridSkeleton } from '../ui/PageRouteSkeleton';

const HeaderContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  padding: 4px 24px 8px 24px;
  margin-bottom: 16px;
`;

const AddButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: transparent;
  border: none;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  color: ${props => props.theme.primary};
  border-radius: 6px;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${props => props.theme.primary}10;
  }
  
  svg {
    width: 16px;
    height: 16px;
  }
`;

const CardsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;
  width: 100%;

  @media (min-width: 768px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (min-width: 1280px) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
`;

const RoleCard = styled(Card)`
  position: relative;
  transition: all 0.2s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: ${props => props.theme.shadowLg};
  }
`;

const CardHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 12px;
`;

const CardTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: ${props => props.theme.foreground};
  margin: 0;
`;

const CardDescription = styled.p`
  font-size: 14px;
  color: ${props => props.theme.mutedForeground};
  margin: 8px 0 16px 0;
  line-height: 1.5;
`;

const CardFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-top: 16px;
  border-top: 1px solid ${props => props.theme.border};
  margin-top: 16px;
`;

const CardMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const MetaItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: ${props => props.theme.mutedForeground};
  
  svg {
    width: 16px;
    height: 16px;
  }
`;

const MetaValue = styled.span`
  font-weight: 500;
  color: ${props => props.theme.foreground};
`;

const ActionButton = styled.button`
  padding: 8px;
  height: auto;
  background: transparent;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  transition: all 0.2s ease;
  color: ${props => props.theme.foreground};
  
  &:hover {
    background: ${props => props.theme.muted};
  }
`;

const EmptyState = styled.div`
  padding: 48px 24px;
  text-align: center;
  background: ${props => props.theme.card};
  border: 1px solid ${props => props.theme.border};
  border-radius: 12px;
`;

const EmptyStateTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: ${props => props.theme.foreground};
  margin: 0 0 8px 0;
`;

const EmptyStateDescription = styled.p`
  font-size: 14px;
  color: ${props => props.theme.mutedForeground};
  margin: 0;
`;

interface Role {
  id: string;
  name: string;
  description: string;
  userCount: number;
  type: string;
  createdAt: string;
  permissions: Record<string, boolean>;
}

export const RolesTable: React.FC = () => {
  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingRole, setEditingRole] = useState<Role | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);

  const fetchRoles = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await apiService.getRoles();
      setRoles(res.data?.roles ?? []);
    } catch (err: any) {
      console.error('Error fetching roles:', err);
      setError(err.response?.data?.error || err.message || 'Failed to load roles');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRoles();
  }, []);

  const getTypeBadgeVariant = (type: string) => {
    return type === 'System' ? 'default' : 'secondary';
  };

  const handleEditRole = (role: Role) => {
    setEditingRole(role);
    setIsEditModalOpen(true);
  };

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    setEditingRole(null);
  };

  const handleEditSuccess = () => {
    fetchRoles(); // Refresh roles after successful edit
  };

  const handleAddRole = () => {
    setIsAddModalOpen(true);
  };

  const handleCloseAddModal = () => {
    setIsAddModalOpen(false);
  };

  const handleAddSuccess = () => {
    fetchRoles(); // Refresh roles after successful add
  };

  return (
    <>
      <HeaderContainer>
        <AddButton onClick={handleAddRole}>
          <Plus size={16} />
          Add Role
        </AddButton>
      </HeaderContainer>
      
      {loading ? (
        <TeamRolesGridSkeleton />
      ) : error ? (
        <EmptyState>
          <EmptyStateTitle>Error</EmptyStateTitle>
          <EmptyStateDescription>{error}</EmptyStateDescription>
        </EmptyState>
      ) : roles.length === 0 ? (
        <EmptyState>
          <EmptyStateTitle>No roles found</EmptyStateTitle>
          <EmptyStateDescription>
            Available roles: Agent and Caretaker. These roles will appear here once you have users assigned to them.
          </EmptyStateDescription>
        </EmptyState>
      ) : (
        <CardsGrid>
          {roles.map((role) => (
            <RoleCard key={role.name} padding="lg" hover>
              <CardHeader>
                <div>
                  <CardTitle>{role.name}</CardTitle>
                  <Badge variant={getTypeBadgeVariant(role.type)} style={{ marginTop: '8px' }}>
                    {role.type}
                  </Badge>
                </div>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <ActionButton>
                      <MoreHorizontal size={16} />
                    </ActionButton>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => handleEditRole(role)}>
                      <Edit size={16} style={{ marginRight: 8 }} />
                      Edit Permissions
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem variant="destructive">
                      <Trash2 size={16} style={{ marginRight: 8 }} />
                      Delete
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </CardHeader>
              
              <CardDescription>{role.description}</CardDescription>
              
              <CardFooter>
                <CardMeta>
                  <MetaItem>
                    <Users size={16} />
                    <span>Users:</span>
                    <MetaValue>{role.userCount}</MetaValue>
                  </MetaItem>
                </CardMeta>
              </CardFooter>
            </RoleCard>
          ))}
        </CardsGrid>
      )}
      
      {editingRole && (
        <EditRoleModal
          isOpen={isEditModalOpen}
          onClose={handleCloseEditModal}
          onSuccess={handleEditSuccess}
          roleName={editingRole.name}
          currentPermissions={editingRole.permissions}
        />
      )}
      
      <AddRoleModal
        isOpen={isAddModalOpen}
        onClose={handleCloseAddModal}
        onSuccess={handleAddSuccess}
      />
    </>
  );
};

